// fixMembershipID.js
// Utility to check and fix membership ID field naming issues

import { collection, getDocs, doc, getDoc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { generateMembershipID } from './generateMembershipID';

/**
 * Check and fix membership ID for a specific user
 * @param {string} userId - User ID to check
 * @returns {Promise<Object>} Result of the fix operation
 */
export const fixUserMembershipID = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnapshot = await getDoc(userRef);
    
    if (!userSnapshot.exists()) {
      return { success: false, message: 'User not found' };
    }
    
    const userData = userSnapshot.data();
    console.log('Current user data:', userData);
    
    // Check if membershipID exists with correct casing
    if (!userData.membershipID || userData.membershipID === '' || userData.membershipID === 'N/A') {
      // Check if there's a lowercase version
      if (userData.membershipId) {
        // Fix casing
        await updateDoc(userRef, {
          membershipID: userData.membershipId,
          membershipId: null // Remove old field
        });
        return { 
          success: true, 
          message: 'Fixed field casing from membershipId to membershipID',
          membershipID: userData.membershipId 
        };
      } else {
        // Generate new membership ID
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('membershipID', '!=', ''));
        const snapshot = await getDocs(q);
        
        const membershipNumbers = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.membershipID) {
            const match = data.membershipID.match(/\d+$/);
            if (match) {
              membershipNumbers.push(parseInt(match[0]));
            }
          }
        });
        
        const maxNumber = membershipNumbers.length > 0 ? Math.max(...membershipNumbers) : 0;
        const nextNumber = maxNumber + 1;
        const newMembershipID = generateMembershipID('KOS', nextNumber);
        
        await updateDoc(userRef, {
          membershipID: newMembershipID
        });
        
        return { 
          success: true, 
          message: 'Generated new membership ID',
          membershipID: newMembershipID 
        };
      }
    }
    
    return { 
      success: true, 
      message: 'Membership ID already exists',
      membershipID: userData.membershipID 
    };
  } catch (error) {
    console.error('Error fixing membership ID:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Fix all users with missing or incorrectly named membership IDs
 * @returns {Promise<Object>} Summary of fixes applied
 */
export const fixAllMembershipIDs = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    const results = {
      total: snapshot.size,
      fixed: 0,
      generated: 0,
      alreadyCorrect: 0,
      errors: []
    };
    
    // Get all existing membership numbers first
    const existingNumbers = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.membershipID) {
        const match = data.membershipID.match(/\d+$/);
        if (match) {
          existingNumbers.push(parseInt(match[0]));
        }
      }
    });
    
    let nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    
    for (const docSnapshot of snapshot.docs) {
      const data = docSnapshot.data();
      const userId = docSnapshot.id;
      
      try {
        // Check if membershipID exists and is valid
        if (!data.membershipID || data.membershipID === '' || data.membershipID === 'N/A') {
          // Check for lowercase version
          if (data.membershipId) {
            await updateDoc(doc(db, 'users', userId), {
              membershipID: data.membershipId,
              membershipId: null
            });
            results.fixed++;
            console.log(`Fixed casing for user ${userId}`);
          } else {
            // Generate new ID
            const newMembershipID = generateMembershipID('KOS', nextNumber);
            await updateDoc(doc(db, 'users', userId), {
              membershipID: newMembershipID
            });
            results.generated++;
            nextNumber++;
            console.log(`Generated ${newMembershipID} for user ${userId}`);
          }
        } else {
          results.alreadyCorrect++;
        }
      } catch (error) {
        console.error(`Error processing user ${userId}:`, error);
        results.errors.push({ userId, error: error.message });
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error fixing all membership IDs:', error);
    throw error;
  }
};
